<template>
  <div>
    <v-card flat class="card-bg">
      <v-card flat class="ma-4 rounded-20">
        <v-card-title class="title-font">
          ORDER FORM
          <v-spacer />
          <v-btn class="my-auto" icon :to="{ name: 'inboxPage' }">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </v-card>
      <v-card flat class="ma-4 rounded-20">
        <v-card-text align="center" v-if="initialising">
          <LoadingSpinner />
        </v-card-text>
        <v-card-text v-if="!initialising">
          <v-tabs flat v-model="orderTab">
            <v-tab href="#details"> Details </v-tab>
            <v-tab
              v-for="dentate in dentates"
              :key="dentate + 'tab'"
              :href="'#' + dentate"
              :disabled="
                orderTab === 'details' ||
                (orderTab === 'upper' &&
                  dentate === 'lower' &&
                  dentates.length === 2)
              "
            >
              {{ capitalise(dentate) }}
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="orderTab" class="pt-5">
            <v-tab-item value="details">
              <v-form ref="orderFormDetails">
                <v-row v-if="settings.manage_clients">
                  <v-col>
                    <v-autocomplete
                      v-model="form.client_uid"
                      :items="clientItems"
                      outlined
                      dense
                      label="Client (Required)"
                      :rules="rule"
                      :menu-props="{ rounded: '10' }"
                    />
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      class="btn-primary"
                      text
                      @click="newClientDialog = true"
                    >
                      New
                    </v-btn>
                    <ClientModal
                      v-model="newClientDialog"
                      :client="newClient"
                      form-title="New Client"
                      :loading="loading"
                      @close="closeNewClientModal"
                      @save="saveNewClient"
                    />
                  </v-col>
                </v-row>
                <v-text-field
                  v-if="!settings.manage_clients"
                  v-model="form.client_name"
                  outlined
                  dense
                  label="Client (Required)"
                  :rules="rule"
                />
                <v-text-field
                  v-model="form.patient_reference"
                  outlined
                  dense
                  label="Patient Reference (Required)"
                  :rules="rule"
                />

                <NewOrderHasGaugeScans v-model="form.gauge_scans" />
                <v-expand-transition>
                  <ScanGaugeKitSelector
                    v-show="form.gauge_scans"
                    v-model="form.kit_box_id"
                    :has-gauge-scans="form.gauge_scans"
                  />
                </v-expand-transition>
                <v-expand-transition>
                  <NewOrderIsPhotogrammetry
                    v-show="
                      form.gauge_scans === false &&
                      currentTenant.photogrammetry_enabled
                    "
                    v-model="form.is_photogrammetry"
                    :has-gauge-scans="form.gauge_scans"
                  />
                </v-expand-transition>

                <NewOrderCaseType
                  v-model="form.immediate"
                  v-if="settings.immediate_workflow"
                />

                <NewOrderDentate v-model="form.dentate" />
              </v-form>
            </v-tab-item>

            <v-tab-item
              v-for="dentate in dentates"
              :key="dentate"
              :value="dentate"
            >
              <v-form :ref="'orderForm' + capitalise(dentate)">
                <template
                  v-if="
                    scanGaugeKits?.length ||
                    currentTenant.photogrammetry_enabled
                  "
                >
                  <NewOrderScanUploadAndValidate
                    v-show="form.gauge_scans"
                    :loading="loading"
                    :dentate="dentate"
                    :sessionId="getField(`${dentate}NcSessionId`)"
                    :leftToRight="getField(`${dentate}LeftToRightScan`)"
                    :rightToLeft="getField(`${dentate}RightToLeftScan`)"
                    :leftToRightUpload="getField(`${dentate}LeftToRightUpload`)"
                    :rightToLeftUpload="getField(`${dentate}RightToLeftUpload`)"
                    :validateScansRes="
                      getField(`${dentate}ValidateScansResponse`)
                    "
                    :immediate="form.immediate"
                    :skip-validation="skipValidation"
                    @update="updateInstance"
                  />

                  <AdditionalFileUploader
                    :dentate="dentate"
                    :scanFiles="getField(`${dentate}ScanFileUploads`)"
                    :photoFiles="getField(`${dentate}PhotoFileUploads`)"
                    :otherFiles="getField(`${dentate}OtherFileUploads`)"
                    @updateFiles="updateInstance"
                  />

                  <NewOrderOrderForm v-model="orderForms[dentate]" />
                </template>
              </v-form>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="btn-error"
            :to="{ name: 'inboxPage' }"
            :disabled="loading || initialising"
          >
            Close
          </v-btn>
          <v-btn
            v-if="
              orderTab === 'details' ||
              orderTab !== dentates[dentates.length - 1]
            "
            text
            class="ma-2 btn-primary rounded-10"
            @click="continueOrder"
            :loading="loading"
            :disabled="
              loading ||
              initialising ||
              (!scanGaugeKits?.length && !currentTenant.photogrammetry_enabled)
            "
          >
            Continue
          </v-btn>

          <v-btn
            v-if="
              orderTab !== 'details' &&
              orderTab === dentates[dentates.length - 1]
            "
            text
            class="ma-2 btn-primary rounded-10"
            @click="createOrder"
            :loading="loading"
            :disabled="
              loading ||
              initialising ||
              (!scanGaugeKits?.length && !form.is_photogrammetry)
            "
          >
            Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-card>
    <v-dialog
      v-model="submitting"
      width="350px"
      persistent
      content-class="rounded-20"
    >
      <v-card class="pb-4">
        <v-card-title class="off">
          <h2 class="title-h2 primary--text">PROCESSING</h2>
        </v-card-title>
        <v-card-text class="text-center py-0">
          <LoadingSpinner class="mt-4" />
        </v-card-text>
        <v-card-text class="title-font pb-0 mt-2">
          {{ dentateUploading ? capitalise(dentateUploading) : null }} File
          Uploads:
        </v-card-text>
        <v-card class="rounded-20 mx-4 py-2" flat outlined>
          <v-card-text class="title-font py-2"
            >Scan Gauge Scans:<v-progress-linear
              :color="scanGaugeUploadProgress === 100 ? 'success' : 'primary'"
              :value="scanGaugeUploadProgress"
            ></v-progress-linear
          ></v-card-text>
          <v-card-text
            class="title-font py-2"
            v-if="upperScanFileUploads.length || lowerScanFileUploads.length"
            >Scans:<v-progress-linear
              :color="scanUploadProgress === 100 ? 'success' : 'primary'"
              :value="scanUploadProgress"
            ></v-progress-linear
          ></v-card-text>
          <v-card-text
            class="title-font py-2"
            v-if="upperPhotoFileUploads.length || lowerPhotoFileUploads.length"
            >Photos:<v-progress-linear
              :color="photoUploadProgress === 100 ? 'success' : 'primary'"
              :value="photoUploadProgress"
            ></v-progress-linear
          ></v-card-text>
          <v-card-text
            class="title-font py-2"
            v-if="upperOtherFileUploads.length || lowerOtherFileUploads.length"
            >Other:<v-progress-linear
              :color="otherUploadProgress === 100 ? 'success' : 'primary'"
              :value="otherUploadProgress"
            ></v-progress-linear
          ></v-card-text>
        </v-card>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { classifyValidationScore } from "@osteon-nexus-connect-lib/nc-frontend"
import { mapActions, mapGetters } from "vuex"
import { capitalise, getScanAnalysis } from "@/utils"
import client from "@/lib/ApiClient"
import ClientModal from "@/components/ClientPage/ClientModal.vue"
import ScanGaugeKitSelector from "./NewOrder/ScanGaugeKitSelector.vue"
import AdditionalFileUploader from "./NewOrder/AdditionalFileUploader.vue"
import NewOrderOrderForm from "./NewOrder/NewOrderOrderForm.vue"
import NewOrderScanUploadAndValidate from "./NewOrder/NewOrderScanUploadAndValidate.vue"
import NewOrderCaseType from "./NewOrder/NewOrderCaseType.vue"
import NewOrderDentate from "./NewOrder/NewOrderDentate.vue"
import NewOrderHasGaugeScans from "./NewOrder/NewOrderHasGaugeScans.vue"
import NewOrderIsPhotogrammetry from "./NewOrder/NewOrderIsPhotogrammetry.vue"

export default {
  name: "NewOrder",

  components: {
    NewOrderOrderForm,
    ClientModal,
    ScanGaugeKitSelector,
    AdditionalFileUploader,
    NewOrderScanUploadAndValidate,
    NewOrderCaseType,
    NewOrderDentate,
    NewOrderHasGaugeScans,
    NewOrderIsPhotogrammetry
  },

  data() {
    return {
      capitalise,
      initialising: true,
      loading: false,
      submitting: false,
      orderTab: 0,
      upperNcSessionId: null,
      upperLeftToRightScan: null,
      upperRightToLeftScan: null,
      upperLeftToRightUpload: null,
      upperRightToLeftUpload: null,
      upperValidateScansResponse: null,
      upperScanFileUploads: [],
      upperPhotoFileUploads: [],
      upperOtherFileUploads: [],
      lowerNcSessionId: null,
      lowerLeftToRightScan: null,
      lowerRightToLeftScan: null,
      lowerLeftToRightUpload: null,
      lowerRightToLeftUpload: null,
      lowerValidateScansResponse: null,
      lowerScanFileUploads: [],
      lowerPhotoFileUploads: [],
      lowerOtherFileUploads: [],
      newClientDialog: false,
      newClient: {},
      form: {},
      orderForms: { upper: { responses: [] }, lower: { responses: [] } },
      failedUploads: [],
      dentateUploading: null,
      scanGaugeUploadProgress: 0,
      scanUploadProgress: 0,
      photoUploadProgress: 0,
      otherUploadProgress: 0,
      rule: [(v) => !!v || "Required"]
    }
  },

  watch: {
    clients(val, prevVal) {
      if (val && this.settings && prevVal === null) {
        this.initialising = false
      }
    },

    settings(val, prevVal) {
      if (val && this.clients && prevVal === null) {
        this.initialising = false
      }
    },

    scanGaugeKits(val) {
      if (val?.length === 0 && !this.currentTenant.photogrammetry_enabled)
        this.$router.push({ name: "inboxPage" })
      if (val?.length === 1) this.form.kit_box_id = val[0].kit_box_id
    },

    "form.gauge_scans"(val) {
      if (val) {
        if (!this.form.kit_box_id && this.scanGaugeKits.length === 1) {
          this.form.kit_box_id = this.scanGaugeKits[0].kit_box_id
        }

        if (this.form.is_photogrammetry) this.form.is_photogrammetry = false
      }
      if (val === false) this.form.kit_box_id = null
    },

    "form.is_photogrammetry"(val) {
      if (val && this.form.kit_box_id) this.form.kit_box_id = null
    }
  },

  computed: {
    ...mapGetters([
      "clients",
      "settings",
      "scanGaugeKits",
      "token",
      "country",
      "featureFlags",
      "currentTenant"
    ]),

    dentates() {
      const dentates = []
      if (["upper", "both"].includes(this.form.dentate)) dentates.push("upper")
      if (["lower", "both"].includes(this.form.dentate)) dentates.push("lower")
      return dentates
    },

    clientItems() {
      return this.users.map((client) => {
        return {
          text: client.name,
          value: client.uid
        }
      })
    },

    users() {
      if (this.clients === null) return []
      return this.clients
    },

    skipValidation() {
      if (this.form.gauge_scans === false) return true
      return false
    }
  },

  methods: {
    ...mapActions(["triggerReload"]),

    getField(field) {
      return this[field]
    },

    continueOrder() {
      let ref = this.$refs["orderForm" + capitalise(this.orderTab)]
      if (Array.isArray(ref)) ref = ref[0]

      const formIsValid = ref.validate()
      const tabs = ["details", ...this.dentates]
      const currentIndex = tabs.findIndex((t) => t === this.orderTab)
      if (formIsValid) this.orderTab = tabs[currentIndex + 1]
      else this.scrollToFirstInvalidField(ref)
    },

    scrollToFirstInvalidField(element, tab) {
      const children = element?.$children
      if (!children || !children?.length) return

      for (let child of children) {
        if (Object.keys(child).includes("valid") && !child.valid) {
          if (tab) this.orderTab = tab
          this.$nextTick(() => {
            const element = child.$el
            element.scrollIntoView({ behavior: "smooth", block: "center" })
          })
          return null
        }
        if (this.scrollToFirstInvalidField(child, tab) === null) return null
      }
    },

    async createOrder() {
      const formResults = ["details", ...this.dentates].reduce((acc, str) => {
        let ref = this.$refs["orderForm" + capitalise(str)]
        if (Array.isArray(ref)) ref = ref[0]
        const formIsValid = ref.validate()
        acc[str] = formIsValid
        return acc
      }, {})
      const formsAreValid = Object.values(formResults).every((r) => r === true)

      let scansPass = false
      if (!this.skipValidation) {
        scansPass = this.dentates.every(
          (dentate) =>
            classifyValidationScore(
              this[`${dentate}ValidateScansResponse`],
              this.form.immediate
            ) !== "Fail"
        )
      }

      if (formsAreValid && (scansPass || this.skipValidation)) {
        this.submitting = true
        this.loading = true
        const orderPayload = {
          ...this.form,
          session_type: this.form.immediate ? "Immediate" : "Definitive"
        }
        for (let dentate of this.dentates) {
          orderPayload[`${dentate}_options`] = {
            nc_session_id: this[`${dentate}NcSessionId`],
            responses: this.orderForms[dentate].responses,
            scan_analysis: !this.skipValidation
              ? getScanAnalysis(this[`${dentate}ValidateScansResponse`])
              : null
          }
        }
        await client.instance.orders
          .createOrder(orderPayload)
          .then(async (order) => {
            for (let session of order.sessions) {
              this.dentateUploading = session.dentate
              if (!this.skipValidation) {
                this.scanGaugeUploadProgress = 100
                await this.uploadFiles(
                  order.uid,
                  session.uid,
                  [
                    this[`${session.dentate}LeftToRightUpload`],
                    this[`${session.dentate}RightToLeftUpload`]
                  ],
                  "scan",
                  "scan_gauge_scan"
                )
              }
              await this.uploadFiles(
                order.uid,
                session.uid,
                this[`${session.dentate}ScanFileUploads`],
                "scan"
              )
              await this.uploadFiles(
                order.uid,
                session.uid,
                this[`${session.dentate}PhotoFileUploads`],
                "photo"
              )
              await this.uploadFiles(
                order.uid,
                session.uid,
                this[`${session.dentate}OtherFileUploads`],
                "other"
              )
            }
            this.dentateUploading = null
          })
          .catch(() => {
            this.submitting = false
            this.loading = false
            // Error handling?
            return console.error("Submission Failed")
          })
        this.submitting = false
        this.loading = false
        this.$router.push({ name: "inboxPage" })
        this.$emit("submission")
      } else {
        const firstInvalidTab = Object.keys(formResults).find(
          (tab) => formResults[tab] === false
        )
        let ref = this.$refs["orderForm" + capitalise(firstInvalidTab)]
        if (Array.isArray(ref)) ref = ref[0]
        this.scrollToFirstInvalidField(ref, firstInvalidTab)
      }
    },

    async uploadFiles(
      orderUid,
      orderSessionUid,
      files,
      fileType,
      fileCategory = null
    ) {
      if (!files.length) return

      let uploadedFiles = []
      if (!fileCategory) {
        uploadedFiles = await client.oft.uploadFiles(files, (progress) => {
          if (fileType === "scan") this.scanUploadProgress = progress
          if (fileType === "photo") this.photoUploadProgress = progress
          if (fileType === "other") this.otherUploadProgress = progress
        })
      } else if (fileCategory === "scan_gauge_scan") {
        // Scan Gauge Scans already uploaded to oft
        uploadedFiles = files
      }

      let payload = []

      if (uploadedFiles) {
        uploadedFiles.forEach((file) => {
          if (file.uploaded) {
            let payloadObj = {
              file_name: file.oftFileName,
              file_type: fileType,
              oft_file_uid: file.oftUid
            }
            if (fileCategory) payloadObj.file_category = fileCategory

            payload.push(payloadObj)
          } else this.failedUploads.push(file)
        })
      }

      await client.instance.orders.addFiles(orderUid, orderSessionUid, payload)
    },

    updateInstance(key, value) {
      this[key] = value
    },

    closeNewClientModal() {
      this.newClient = {}
      this.newClientDialog = false
    },

    async saveNewClient(clientForm, close = true) {
      this.loading = true
      const newClient = await client.instance.clients.createClient(clientForm)
      this.form.client_uid = newClient.uid
      this.triggerReload("clients")
      this.loading = false
      if (close) this.closeNewClientModal()
    }
  },

  async mounted() {
    this.initialising = true
    if (this.clients && this.settings) this.initialising = false
    else {
      this.triggerReload("clients")
      this.triggerReload("settings")
    }
  }
}
</script>
